/**
 * Central color registry — the SINGLE source of truth for every chart and
 * table accent color in the app (business constants — convention section 34;
 * the library stays business-free, these hex values never enter
 * `echart-table-library`). One palette per domain so a color is managed in
 * one place and renders identically on every page/widget. Widgets import
 * their palette from here instead of hardcoding hex values.
 */

// ---------------------------------------------------------------------------
// Shared chrome (all widgets)
// ---------------------------------------------------------------------------

/** Chart chrome: export/canvas background and selection borders. */
export const CHART_CHROME = {
  /** Export / canvas background (the white card). */
  background: "#ffffff",
  /** Border drawn around neighbouring bars (print-volume charts). */
  selectionBorder: "#999999",
} as const;

// ---------------------------------------------------------------------------
// Machine statuses (Running Status page + dashboard AverageRatePie)
// ---------------------------------------------------------------------------

/**
 * Canonical machine-status palette — one fixed color per status, shared by
 * every machine widget (timeline, share bars, average-rate pie) so a status
 * renders identically across charts. The four core running statuses
 * (Print / Adjustment / Cleaning / Not Active) follow the pie/share spec
 * palette; Error / ON / OFF extend it for the timeline.
 */
export const MACHINE_STATUS_COLORS = {
  Print: "#73c0de",
  Adjustment: "#3ba272",
  Cleaning: "#fac858",
  "Not Active": "#cccccc",
  Error: "#ee6666",
  ON: "#5470c6",
  OFF: "#000000",
} as const;

// ---------------------------------------------------------------------------
// Ink channels (Dashboard print-volume / ink-trend / KPI dots + Paper Usage)
// ---------------------------------------------------------------------------

/**
 * Print-ink chart palette. One fixed color per series so every chart on the
 * dashboard speaks the same visual language. Widgets pass these through
 * their EChart config's `colors` (config beats preset palette).
 */
export const INK_CHART_COLORS = {
  C: "#00ffee",
  M: "#fe00fe",
  Y: "#fefe00",
  K: "#000000",
  MICR: "#824000",
  Jobcount: "#339966",
  "Printed Length": "#5cbae5",
} as const;

/** Ink channel order shared by the bar and pie dashboard widgets. */
export const INK_CHANNELS = ["C", "M", "Y", "K", "MICR"] as const;

export type InkChannel = (typeof INK_CHANNELS)[number];

/** Human-readable channel names for axis labels (same order as INK_CHANNELS). */
export const INK_CHANNEL_NAMES: Record<InkChannel, string> = {
  C: "Cyan (C)",
  M: "Magenta (M)",
  Y: "Yellow (Y)",
  K: "Black (K)",
  MICR: "MICR",
};

// ---------------------------------------------------------------------------
// Paper Usage page (both charts + table progress cells)
// ---------------------------------------------------------------------------

/** Chart palette: bar shades, share segments and the Job Count line. */
export const PAPER_USAGE_CHART_COLORS = {
  /** Unselected total bar (both charts). */
  barBase: "#6bb9f0",
  /** Selection shade of the base bar — a deep saturated blue that clearly
   *  stands out from the light-blue base. */
  barHighlight: "#1d4ed8",
  /** Bottom stacked segment = the selected width/color's share. */
  shareBlue: "#1e78d2",
  /** Selection shade of the share segment (one step deeper). */
  shareHighlight: "#0c3fae",
  /** Top stacked segment = the remainder. */
  remainderGrey: "#d4d4d8",
  /** Selection shade of the remainder segment (clearly darker). */
  remainderHighlight: "#71717a",
  /** Job Count line (secondary axis). */
  line: "#2e8b57",
} as const;

// NOTE: the dark tooltip chrome (dark-grey box, white text) is the LIBRARY
// default now (`tooltipChromeDefaults` in `echart-table-library`) — one
// source of truth for every chart, so no per-page tooltip color constants.

/** Fill color per table measure column (the ink channels and their measures
 *  use the canonical `INK_CHART_COLORS` accents — derived, so a channel
 *  renders identically on the Dashboard and Paper Usage pages). Consumed by
 *  the library's `progressColumn` display via the column config. */
export const PAPER_USAGE_PROGRESS_COLORS = {
  printedLength: INK_CHART_COLORS["Printed Length"],
  jobCount: INK_CHART_COLORS.Jobcount,
  cLength: INK_CHART_COLORS.C,
  mLength: INK_CHART_COLORS.M,
  yLength: INK_CHART_COLORS.Y,
  kLength: INK_CHART_COLORS.K,
  /** MICR channel — distinct from the magenta M(L). */
  micrLength: INK_CHART_COLORS.MICR,
} as const;

export type PaperUsageProgressColumnId = keyof typeof PAPER_USAGE_PROGRESS_COLORS;

// ---------------------------------------------------------------------------
// Print Overview page (both charts: current vs. last-year comparison)
// ---------------------------------------------------------------------------

/**
 * Print-overview palette: side-by-side per period, the CURRENT value in the
 * dark shade and LAST YEAR's in the light shade — blue bars (Printed
 * Length, left axis), green lines (Job Count / Ink Usage, right axis).
 */
export const PRINT_OVERVIEW_CHART_COLORS = {
  /** Printed Length bar — current period. */
  barBase: "#5cbae5",
  /** Printed Length bar — same period last year. */
  barLastYear: "#b3ddf5",
  /** Selection accent of the highlighted column (both charts' bars). */
  barHighlight: "#1d4ed8",
  /** Metric line — current period (Job Count / Ink Usage). */
  lineBase: "#2e8b57",
  /** Metric line — same period last year. */
  lineLastYear: "#8fd3a8",
  /** Soft glow under the highlighted column (paper-usage parity). */
  highlightGlow: {
    shadowBlur: 14,
    shadowColor: "rgba(30, 64, 175, 0.55)",
    shadowOffsetY: 3,
  },
} as const;

// ---------------------------------------------------------------------------
// OEE page
// ---------------------------------------------------------------------------

/** OEE trend series palette: A/P/Q bars + the combined OEE % line. */
export const OEE_SERIES_COLORS = {
  availability: "#2563eb",
  performance: "#16a34a",
  quality: "#f59e0b",
  oeePercent: "#dc2626",
} as const;

// ---------------------------------------------------------------------------
// Basic Information page (table progress cells)
// ---------------------------------------------------------------------------

/** Progress-cell accents of the basic-information table. */
export const BASIC_INFO_PROGRESS_COLORS = {
  capacity: "#10b981",
  completion: "#f59e0b",
} as const;
